import { h, Component, State, Prop, Watch, Listen, Event, EventEmitter } from '@stencil/core';
import { fetchComponentData } from '../../utils/api-utils';
import { HeaderApiResponse } from '../../types/entitites';
import { extractSvg } from '../../utils/svg-utils';
import { getApiEndpoint } from '../../constants';
import i18n from '../../utils/i18n';
import { getCookieLanguage, setCookieLanguage } from '../../utils/cookie-utils';

@Component({
  tag: 'ps-header',
  styleUrl: 'header.styles.scss',
})
export class Header {
  @Prop() domain = 'patentstyret.no';
  @Prop() env?: 'prod' | 'dev' = 'prod';
  @Prop() hash: string;
  @Prop() ttl: number = 300;
  @Prop() color?: 'secondary' | undefined;

  @Prop({ mutable: true, reflect: true }) expanded = false;
  @Prop({ mutable: true, reflect: true }) language?: 'no' | 'en';
  @Prop() languageRouting: boolean = false;

  @State() fetchResult: HeaderApiResponse;

  @Event({ bubbles: true, composed: true }) psOnLanguageChanged: EventEmitter<string>;

  @Prop() showLogin: boolean = false;
  @Prop({ mutable: true, reflect: true }) isLoggedIn = false;
  @Event({ bubbles: true, composed: true }) psOnLoginClicked: EventEmitter<string>;

  mainHeader: HTMLElement;
  firstFocusableElement: HTMLElement;
  lastFocusableElement: HTMLElement;

  @Watch('expanded')
  onExpandedValueChanged(newValue: boolean) {
    if (newValue) {
      this.openHeaderMenu();
    } else {
      this.closeHeaderMenu();
    }
  }

  // Close menu on ESC key press
  @Listen('keydown')
  onKeyDownEvent(event: KeyboardEvent) {
    const isEscPressed = event.key === 'Escape' || event.code === 'Escape';
    if (isEscPressed && this.expanded) {
      this.expanded = !this.expanded;
    }
  }

  @Watch('language')
  onLanguageValueChanged() {
    this.fetchData(true);
  }

  // Listen on event from other web-components
  @Listen('psOnLanguageChanged', { target: 'body' })
  onLanguageChangedEvent(event: CustomEvent) {
    if (event.detail && this.language !== event.detail) {
      this.language = event.detail === 'en' ? 'en' : 'no';
    }
  }

  handleToggleLanguageClick = (e: MouseEvent) => {
    const lang = this.language === 'no' ? 'en' : 'no';
    setCookieLanguage(lang, this.domain);
    if (!this.languageRouting) {
      this.language = lang;
      i18n.changeLanguage(lang);
      this.psOnLanguageChanged.emit(lang);
      e.preventDefault();
    }
  };

  initLanguage() {
    this.language = this.language || getCookieLanguage() || (i18n.language as 'no' | 'en');
    const overwriteBrowserLang = !!this.language && this.language != i18n.language;
    if (overwriteBrowserLang) {
      if (this.language === 'no' || this.language === 'en') {
        i18n.changeLanguage(this.language); // update language
        setCookieLanguage(this.language, this.domain); // set cookie value shared across sub-domains
      }
    }
    // always do an initial emit of language to parent/consumer app
    this.psOnLanguageChanged.emit(this.language);
  }

  componentWillLoad() {
    this.initLanguage();
  }

  componentDidRender() {
    extractSvg();
  }

  // Fetch data when the component is loaded (updated with @Watch language)
  // componentDidLoad() {
  //   this.fetchData();
  // }

  getHeaderEndPoint = () => {
    return `${getApiEndpoint(this.env, this.language)}?header=True`;
  };

  async fetchData(hardReload: boolean = false) {
    const ttl = hardReload ? 0 : this.ttl;
    const url = this.getHeaderEndPoint();
    let key = 'patentstyret-stenciljs-header-component';
    if (this.hash) {
      key = `${this.hash}-${key}`;
    }
    this.fetchResult = await fetchComponentData(url, key, ttl);
  }

  openHeaderMenu() {
    this.mainHeader.classList.add('main-header--menu-open');
    document.body.style.overflow = 'hidden';

    // Detect first and last focusable elements
    const focusableElements = this.mainHeader.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

    this.firstFocusableElement = focusableElements[0] as HTMLElement;
    this.lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    // Add event listeners to trap focus inside mainHeader
    this.firstFocusableElement.addEventListener('keydown', event => this.trapHeaderFocus(event));
    this.lastFocusableElement.addEventListener('keydown', event => this.trapHeaderFocus(event));
  }

  closeHeaderMenu() {
    this.mainHeader.classList.remove('main-header--menu-open');
    document.body.style.overflow = '';

    // Remove event listeners when the menu is closed
    this.firstFocusableElement.removeEventListener('keydown', event => this.trapHeaderFocus(event));
    this.lastFocusableElement.removeEventListener('keydown', event => this.trapHeaderFocus(event));
  }

  trapHeaderFocus(event: KeyboardEvent) {
    const isTabPressed = event.key === 'Tab' || event.keyCode === 9;
    if (!isTabPressed) return;

    if (event.shiftKey) {
      // If shift key is pressed, focus the last focusable element
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // If shift key is not pressed, focus the first focusable element
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  loginClickHandler = (e: MouseEvent) => {
    if (!this.languageRouting) {
      this.psOnLoginClicked.emit(this.isLoggedIn ? 'logout' : 'login');
      e.preventDefault();
    }
  };

  getContent = (data: HeaderApiResponse) => {
    const { accessibility, assets, siteLink, header, search, strings, languageLink } = data;

    let loginBtnLabel = 'Login';
    if (this.isLoggedIn) {
      loginBtnLabel = 'Logout';
    }

    return (
      <div class="container">
        <div class="row">
          <div class="col-12 main-header__wrapper">
            {/*Skip to main link */}
            <div class="arrow-link main-header__skip">
              <a href="#main">
                <span data-translate-manager="default-page.links.skip-to-main">{strings.skipToMainLabel}</span>
                <span class="svg-icon" innerHTML={assets.images.arrowDown}></span>
              </a>
            </div>

            {/*Logo */}
            <a href={siteLink.href} class="main-header__logo" aria-label={accessibility.logoLinkText}>
              {header.logo && <img src={header.logo.link} alt={header.logo.alt} />}
            </a>

            {/*Nav links */}

            <ul class="main-header__links">
              {header.links.length > 0 &&
                header.links.map(headerLink => (
                  <li class="arrow-link">
                    <a target={headerLink.url.target} href={headerLink.url.href}>
                      {headerLink.title}
                      <span class="svg-icon" innerHTML={assets.images.arrowRight}></span>
                    </a>
                  </li>
                ))}
              {languageLink && (
                <li class="arrow-link">
                  <a target="_self" href={languageLink.url.href} onClick={this.handleToggleLanguageClick}>
                    {languageLink.title}
                    <span class="svg-icon" innerHTML={assets.images.language}></span>
                  </a>
                </li>
              )}
            </ul>

            {/*Search button */}
            <a href={search.url.href} class="btn btn--secondary main-header__search-btn">
              <span>{strings.searchLabel}</span>
              <span class="svg-icon" innerHTML={assets.images.search}></span>
            </a>

            {/*Menu button */}
            <button class="btn btn--secondary main-header__menu-btn" onClick={() => (this.expanded = !this.expanded)}>
              <span class="text">{strings.menuText}</span>
              <span class="text text--close">{strings.menuCloseText}</span>
              <span aria-hidden="true" class="icon icon--menu">
                <span class="bar"></span>
                <span class="bar"></span>
                <span class="bar"></span>
              </span>
              <span class="svg-icon" innerHTML={assets.images.closeHeader}></span>
            </button>

            {/*Menu content*/}
            <div class="main-header__menu-content">
              {/*Top links*/}
              {header.links.length > 0 && (
                <ul class="main-header__menu-top">
                  {header.links.map(headerLink => {
                    return (
                      <li class="arrow-link arrow-link--hide-xl">
                        <a target={headerLink.url.target} href={headerLink.url.href}>
                          {headerLink.title}
                          <span class="svg-icon" innerHTML={assets.images.arrowRight}></span>
                        </a>
                      </li>
                    );
                  })}
                  {header.menu.myPage.title && (
                    <li class="arrow-link">
                      <a target={header.menu.myPage.url.target} href={header.menu.myPage.url.href}>
                        {header.menu.myPage.title}
                        {header.menu.myPage.url.target == '_self' && <span class="svg-icon" innerHTML={assets.images.arrowRight}></span>}
                        {header.menu.myPage.url.target == '_blank' && <span class="svg-icon" innerHTML={assets.images.external}></span>}
                      </a>
                    </li>
                  )}
                  {this.showLogin && (
                    <li class="arrow-link">
                      <a target="_self" href="#" onClick={this.loginClickHandler}>
                        {loginBtnLabel}
                      </a>
                    </li>
                  )}
                  {languageLink && (
                    <li class="arrow-link  arrow-link--hide-xl">
                      <a target="_self" href={languageLink.url.href} onClick={this.handleToggleLanguageClick}>
                        {languageLink.title}
                        <span class="svg-icon" innerHTML={assets.images.language}></span>
                      </a>
                    </li>
                  )}
                </ul>
              )}

              {/*Primary links*/}
              <ul class="main-header__menu-links">
                {header.menu.mainLinks.map(mainLink => {
                  return (
                    <li class="arrow-link">
                      <a target={mainLink.url.target} href={mainLink.url.href}>
                        {mainLink.url.target == '_self' && <span class="svg-icon start" innerHTML={assets.images.arrowRight}></span>}
                        {mainLink.url.target == '_blank' && <span class="svg-icon start" innerHTML={assets.images.external}></span>}
                        {mainLink.title}
                      </a>
                    </li>
                  );
                })}
              </ul>

              {/*Secondary links */}
              <div class="main-header__menu-list">
                <ul class="link-list">
                  {header.menu.secondaryLinks.map(secondaryLink => {
                    return (
                      <li class="link-list__item">
                        <a target={secondaryLink.url.target} href={secondaryLink.url.href} class="link-list__link">
                          {secondaryLink.url.target == '_self' && <span class="svg-icon" innerHTML={assets.images.arrowRight}></span>}
                          {secondaryLink.url.target == '_blank' && <span class="svg-icon" innerHTML={assets.images.external}></span>}
                          {secondaryLink.title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  getClassNames = () => {
    const classes = ['cms-reset', 'cms-global', 'cms-typography', 'main-header'];
    if (this.color) {
      classes.push('main-header--light-green');
    }
    return classes.join(' ');
  };

  render() {
    const classNames = this.getClassNames();
    let content: JSX.Element | undefined;
    if (this.fetchResult) {
      content = this.getContent(this.fetchResult);
    }

    return (
      <header ref={el => (this.mainHeader = el)} class={classNames}>
        {content}
      </header>
    );
  }
}
